'use client';


import Header from '@/components/common/Header';
import HeroSection from './components/HeroSection';
import CompanyStory from './components/CompanyStory';
import LeadershipTeam from './components/LeadershipTeam';
import CoreValues from './components/CoreValues';
import CompanyCulture from './components/CompanyCulture';
import CertificationsPartnerships from './components/CertificationsPartnerships';
import CTASection from './components/CTASection';
import Footer from './components/Footer';

interface StoryMilestone {
  year: string;
  title: string;
  description: string;
  icon: string;
}

interface TeamMember {
  id: number;
  name: string;
  role: string;
  image: string;
  alt: string;
  bio: string;
  expertise: string[];
  certifications: string[];
}

interface CoreValue {
  id: number;
  icon: string;
  title: string;
  description: string;
  color: string;
}

interface CultureHighlight {
  id: number;
  title: string;
  description: string;
  image: string;
  alt: string;
  icon: string;
}

interface EmployeeTestimonial {
  id: number;
  name: string;
  role: string;
  image: string;
  alt: string;
  quote: string;
}

interface Certification {
  id: number;
  name: string;
  issuer: string;
  logo: string;
  alt: string;
  year: string;
}

interface Partnership {
  id: number;
  name: string;
  type: string;
  logo: string;
  alt: string;
  description: string;
}

interface Award {
  id: number;
  title: string;
  organization: string;
  year: string;
  icon: string;
}

export default function AboutPage() {
  const heroData = {
    title: "Empowering Businesses Through Technology Excellence",
    subtitle: "About Centronics IT Soloutions",
    description: "We're more than an IT services provider—we're your strategic technology partner, combining enterprise-level expertise with personalized service to transform how businesses leverage technology for growth and success."
  };

  const milestones: StoryMilestone[] = [
  {
    year: "2010",
    title: "The Beginning - Dubai",
    description: "Founded in Dubai, UAE with a vision to make enterprise-level IT services accessible to businesses of all sizes. Started with a team of passionate technologists serving the Middle East region.",
    icon: "RocketLaunchIcon"
  },
  {
    year: "2013",
    title: "Rapid Growth",
    description: "Expanded service offerings to include cybersecurity and cloud solutions. Grew our team and established partnerships with major technology vendors across the Gulf region.",
    icon: "ChartBarIcon"
  },
  {
    year: "2016",
    title: "Industry Recognition",
    description: "Achieved SOC 2 Type II certification and ISO 27001 compliance. Recognized as a top IT services provider in the UAE, serving clients across multiple industries.",
    icon: "TrophyIcon"
  },
  {
    year: "2019",
    title: "Digital Transformation",
    description: "Launched comprehensive digital transformation consulting services. Helped businesses navigate cloud migration, automation, and modernization initiatives with measurable ROI.",
    icon: "CpuChipIcon"
  },
  {
    year: "2022",
    title: "Innovation Hub",
    description: "Opened state-of-the-art Security Operations Center (SOC) providing 24/7 monitoring and threat response. Introduced AI-powered predictive maintenance and proactive support.",
    icon: "ShieldCheckIcon"
  },
  {
    year: "2024",
    title: "Expansion to Saudi Arabia",
    description: "Established our presence in Jeddah, Saudi Arabia, bringing 15 years of IT excellence to serve the growing Saudi market with cutting-edge solutions and local expertise.",
    icon: "SparklesIcon"
  }];


  const teamMembers: TeamMember[] = [
  {
    id: 1,
    name: "أحمد العمري",
    role: "Chief Executive Officer",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_101f10bcb-1763296392759.png",
    alt: "Professional Arab businessman in traditional white thobe and modern suit jacket in executive office",
    bio: "Ahmed brings over 20 years of technology leadership experience, having previously served as CTO at Fortune 500 companies. His vision of making enterprise-level IT accessible to all businesses drives Centronics IT Soloutions's mission and growth strategy.",
    expertise: ["Strategic Planning", "Digital Transformation", "Business Development", "Technology Leadership"],
    certifications: ["MBA - Technology Management", "PMP Certified", "ITIL Expert", "Certified Information Systems Security Professional (CISSP)"]
  },
  {
    id: 2,
    name: "محمد السعيد",
    role: "Chief Technology Officer",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1e38866c9-1767405210479.png",
    alt: "Arab technology professional in business attire working on advanced IT infrastructure",
    bio: "Mohammed is a technology visionary with deep expertise in cloud architecture, cybersecurity, and infrastructure design. He leads our technical team in delivering innovative solutions that solve complex business challenges.",
    expertise: ["Cloud Architecture", "Cybersecurity", "Infrastructure Design", "DevOps"],
    certifications: ["AWS Certified Solutions Architect", "Microsoft Azure Expert", "CISSP", "Certified Ethical Hacker (CEH)"]
  },
  {
    id: 3,
    name: "فاطمة الزهراني",
    role: "VP of Client Success",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_186cc5d24-1763296386782.png",
    alt: "Professional Arab businesswoman in elegant hijab and business suit in modern office setting",
    bio: "Fatima's passion for client relationships and operational excellence ensures every client receives exceptional service. She oversees our support teams and drives continuous improvement in service delivery.",
    expertise: ["Client Relations", "Service Delivery", "Process Optimization", "Team Leadership"],
    certifications: ["ITIL Service Management", "Six Sigma Black Belt", "Customer Success Manager Certified", "Project Management Professional (PMP)"]
  },
  {
    id: 4,
    name: "خالد الشمري",
    role: "Director of Cybersecurity",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ef90d015-1767718585326.png",
    alt: "Arab cybersecurity expert in professional attire analyzing security systems on multiple monitors",
    bio: "Khaled leads our cybersecurity practice with expertise gained from government and private sector roles. He ensures our clients stay protected against evolving threats through proactive security strategies.",
    expertise: ["Security Operations", "Threat Intelligence", "Compliance", "Incident Response"],
    certifications: ["CISSP", "CISM", "CEH", "GIAC Security Expert (GSE)"]
  },
  {
    id: 5,
    name: "نورة القحطاني",
    role: "Head of Cloud Solutions",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1ed4016f0-1769355061884.png",
    alt: "Arab woman technology leader in professional hijab presenting cloud architecture solutions",
    bio: "Noura specializes in designing and implementing scalable cloud solutions that drive business agility. Her expertise helps clients leverage cloud technology for competitive advantage and operational efficiency.",
    expertise: ["Cloud Migration", "Multi-Cloud Strategy", "Cost Optimization", "Cloud Security"],
    certifications: ["AWS Solutions Architect Professional", "Google Cloud Professional", "Azure Solutions Architect Expert", "Kubernetes Administrator"]
  },
  {
    id: 6,
    name: "عبدالله الدوسري",
    role: "Director of Managed Services",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13f83e550-1767950970559.png",
    alt: "Arab IT operations director in business attire monitoring systems in modern operations center",
    bio: "Abdullah oversees our 24/7 managed services operations, ensuring clients receive proactive monitoring, maintenance, and support. His focus on automation and efficiency keeps systems running smoothly.",
    expertise: ["IT Operations", "Service Management", "Automation", "Performance Optimization"],
    certifications: ["ITIL Expert", "Microsoft Certified Systems Engineer", "VMware Certified Professional", "Cisco Certified Network Professional"]
  }];


  const coreValues: CoreValue[] = [
  {
    id: 1,
    icon: "ShieldCheckIcon",
    title: "Reliability Without Rigidity",
    description: "We deliver consistent, dependable service while remaining flexible and adaptive to your unique business needs and changing requirements.",
    color: "from-brand-primary to-brand-secondary"
  },
  {
    id: 2,
    icon: "LightBulbIcon",
    title: "Innovation Without Complexity",
    description: "We leverage cutting-edge technology to solve problems, but always prioritize practical solutions that are easy to understand and use.",
    color: "from-accent to-brand-secondary"
  },
  {
    id: 3,
    icon: "AcademicCapIcon",
    title: "Expertise Without Intimidation",
    description: "Our deep technical knowledge empowers rather than overwhelms. We explain complex concepts in accessible language and guide you through every decision.",
    color: "from-brand-primary to-accent"
  },
  {
    id: 4,
    icon: "HandRaisedIcon",
    title: "Partnership Over Transactions",
    description: "We're not just a vendor—we're an extension of your team, invested in your long-term success and committed to building lasting relationships.",
    color: "from-success to-brand-secondary"
  },
  {
    id: 5,
    icon: "ChatBubbleLeftRightIcon",
    title: "Transparent Communication",
    description: "We believe in honest, clear communication. No jargon, no hidden costs, no surprises—just straightforward dialogue and realistic expectations.",
    color: "from-brand-secondary to-accent"
  },
  {
    id: 6,
    icon: "ChartBarIcon",
    title: "Measurable Results",
    description: "Every solution we implement is designed to deliver tangible business value. We track metrics, demonstrate ROI, and continuously optimize for better outcomes.",
    color: "from-brand-primary to-success"
  }];


  const cultureHighlights: CultureHighlight[] = [
  {
    id: 1,
    title: "Continuous Learning & Development",
    description: "We invest heavily in our team's growth through training programs, certifications, conferences, and dedicated learning time. Every team member has an annual professional development budget.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_17a80e87c-1764706018049.png",
    alt: "Diverse team of professionals collaborating in modern training room with digital screens",
    icon: "AcademicCapIcon"
  },
  {
    id: 2,
    title: "Work-Life Integration",
    description: "We understand that great work happens when people are balanced and fulfilled. Flexible schedules, remote work options, and generous time-off policies support our team's well-being.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_19204142d-1764671823198.png",
    alt: "Professional working comfortably from home office with laptop and coffee",
    icon: "ScaleIcon"
  },
  {
    id: 3,
    title: "Innovation Time",
    description: "Every team member dedicates 10% of their time to exploring new technologies, experimenting with solutions, and contributing to internal innovation projects that push our capabilities forward.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1c52aaffc-1764642436173.png",
    alt: "Engineer testing new technology prototype in modern innovation lab",
    icon: "LightBulbIcon"
  },
  {
    id: 4,
    title: "Community Impact",
    description: "We're committed to giving back through pro-bono IT services for nonprofits, STEM education programs for underserved communities, and environmental sustainability initiatives.",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_1209d3869-1767022129961.png",
    alt: "Team members volunteering and teaching technology skills to young students",
    icon: "HeartIcon"
  }];


  const employeeTestimonials: EmployeeTestimonial[] = [
  {
    id: 1,
    name: "Alex Rivera",
    role: "Senior Cloud Engineer",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_100afe953-1763294659192.png",
    alt: "Hispanic man with beard in casual tech attire smiling in modern office",
    quote: "I've worked at several tech companies, but Centronics IT Soloutions stands out for its genuine commitment to both client success and employee growth. The learning opportunities here are unmatched."
  },
  {
    id: 2,
    name: "Emily Watson",
    role: "Security Analyst",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_117824cb7-1768211916695.png",
    alt: "Caucasian woman with red hair in professional attire working at security operations center",
    quote: "The collaborative culture and cutting-edge projects keep me excited to come to work every day. I feel valued, challenged, and supported in equal measure."
  },
  {
    id: 3,
    name: "James Park",
    role: "DevOps Specialist",
    image: "https://img.rocket.new/generatedImages/rocket_gen_img_13319fb64-1763296603356.png",
    alt: "Asian man with glasses in casual shirt working on multiple monitors",
    quote: "The flexibility to work remotely while still feeling connected to the team has been life-changing. Plus, the innovation time policy lets me explore technologies that benefit both my career and our clients."
  }];


  const certifications: Certification[] = [
  {
    id: 1,
    name: "SOC 2 Type II",
    issuer: "AICPA",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1972a3111-1764673547784.png",
    alt: "SOC 2 Type II certification badge",
    year: "2023"
  },
  {
    id: 2,
    name: "ISO 27001",
    issuer: "International Organization for Standardization",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1e32f8ff5-1764670065275.png",
    alt: "ISO 27001 certification emblem",
    year: "2023"
  },
  {
    id: 3,
    name: "AWS Advanced Consulting Partner",
    issuer: "Amazon Web Services",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1b5a6a9a0-1764669837513.png",
    alt: "AWS Advanced Consulting Partner logo",
    year: "2024"
  },
  {
    id: 4,
    name: "Microsoft Gold Partner",
    issuer: "Microsoft",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_10ae3dc39-1769355058622.png",
    alt: "Microsoft Gold Partner badge",
    year: "2024"
  }];


  const partnerships: Partnership[] = [
  {
    id: 1,
    name: "Cisco",
    type: "Gold Partner",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1e9a350d3-1769355057791.png",
    alt: "Cisco company logo",
    description: "Networking and security solutions"
  },
  {
    id: 2,
    name: "VMware",
    type: "Premier Partner",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1e93e68b1-1769355056718.png",
    alt: "VMware company logo",
    description: "Virtualization and cloud infrastructure"
  },
  {
    id: 3,
    name: "Palo Alto Networks",
    type: "Authorized Partner",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1e04c5bee-1764706109733.png",
    alt: "Palo Alto Networks logo",
    description: "Next-generation cybersecurity"
  },
  {
    id: 4,
    name: "Google Cloud",
    type: "Partner",
    logo: "https://img.rocket.new/generatedImages/rocket_gen_img_1696a4234-1764661522844.png",
    alt: "Google Cloud Platform logo",
    description: "Cloud computing and AI solutions"
  }];


  const awards: Award[] = [
  {
    id: 1,
    title: "Best IT Services Provider",
    organization: "Tech Excellence Awards",
    year: "2023",
    icon: "TrophyIcon"
  },
  {
    id: 2,
    title: "Cybersecurity Innovation Award",
    organization: "InfoSec Leadership Council",
    year: "2023",
    icon: "ShieldCheckIcon"
  },
  {
    id: 3,
    title: "Top Workplace Culture",
    organization: "Great Place to Work",
    year: "2024",
    icon: "HeartIcon"
  },
  {
    id: 4,
    title: "Cloud Transformation Leader",
    organization: "Cloud Computing Association",
    year: "2024",
    icon: "CloudIcon"
  }];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main>
        <HeroSection
          title={heroData.title}
          subtitle={heroData.subtitle}
          description={heroData.description} />

        
        <CompanyStory milestones={milestones} />
        
        <LeadershipTeam teamMembers={teamMembers} />
        
        <CoreValues values={coreValues} />
        
        <CompanyCulture
          highlights={cultureHighlights}
          testimonials={employeeTestimonials} />

        
        <CertificationsPartnerships
          certifications={certifications}
          partnerships={partnerships}
          awards={awards} />

        
        <CTASection />
      </main>
      
      <Footer />
    </div>);

}